import { Group, SegmentedControl, Stack, Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import dayjs from "dayjs";

import { CHART_TIME_FRAMES, describeRange } from "./timeFrame";

// Controlled, per-chart time frame control. The parent owns the value
// ({ timeFrame, dateFrom, dateTo }) in local state, so nothing touches the URL.
// Shows the Event/App/Custom segmented control, plus date pickers when Custom.
function ChartTimeFrameControl({ value, onChange, event }) {
  const timeFrame = value?.timeFrame ?? "event";
  const dateFrom = value?.dateFrom ?? "";
  const dateTo = value?.dateTo ?? "";

  // Custom ranges reach back to event creation, not activation
  const createdAtDate = event?.createdAt
    ? new Date(event.createdAt)
    : undefined;
  const endDateMin = dateFrom ? new Date(dateFrom) : createdAtDate;

  const handleTimeFrameChange = (next) => {
    if (next === "custom") onChange({ timeFrame: "custom", dateFrom, dateTo });
    else onChange({ timeFrame: next });
  };

  const handleDateChange = (key) => (date) => {
    const str = date ? dayjs(date).format("YYYY-MM-DD") : "";
    onChange({
      timeFrame: "custom",
      dateFrom: key === "dateFrom" ? str : dateFrom,
      dateTo: key === "dateTo" ? str : dateTo,
    });
  };

  const showPickers = timeFrame === "custom";
  const incompleteCustom = timeFrame === "custom" && (!dateFrom || !dateTo);

  // Single caption shown on screen and in print: the interactive control is
  // hidden when printing, so the caption carries the selected time frame.
  const caption = describeRange({ timeFrame, dateFrom, dateTo }, event);

  return (
    <Stack gap={2} align="flex-end">
      <Group
        className="insights-print-hide"
        gap="xs"
        align="center"
        wrap="wrap"
        justify="flex-end"
      >
        {showPickers && (
          <>
            <DatePickerInput
              size="xs"
              w={130}
              placeholder="Start date"
              value={dateFrom ? new Date(dateFrom) : null}
              onChange={handleDateChange("dateFrom")}
              minDate={createdAtDate}
              maxDate={dateTo ? new Date(dateTo) : new Date()}
              valueFormat="YYYY-MM-DD"
              clearable
            />
            <DatePickerInput
              size="xs"
              w={130}
              placeholder="End date"
              value={dateTo ? new Date(dateTo) : null}
              onChange={handleDateChange("dateTo")}
              minDate={endDateMin}
              maxDate={new Date()}
              valueFormat="YYYY-MM-DD"
              clearable
            />
          </>
        )}
        <SegmentedControl
          size="xs"
          value={timeFrame}
          onChange={handleTimeFrameChange}
          data={CHART_TIME_FRAMES}
        />
      </Group>
      {incompleteCustom ? (
        <Text className="insights-print-hide" size="xs" c="dimmed">
          Pick a start and end date to apply the custom range
        </Text>
      ) : (
        caption && (
          <Text size="xs" c="dimmed" ta="right">
            {caption}
          </Text>
        )
      )}
    </Stack>
  );
}

export default ChartTimeFrameControl;
